import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material';
import { FormProvider } from 'react-hook-form';
import PalmettoSideMenu from '@zawarski/palmetto-react-side-menu';
import { CircleDashed, SquareUser, TriangleAlert, Users } from 'lucide-react';
import Logo from '@/assets/img.png';
import { useAppDispatch, useAppSelector } from 'store/hooks.ts';
import { resetAuthToken } from 'store/feature/authToken/authToken.action.ts';
import {
  accountGroupIDSelector,
  allGroupsSelector,
  parentGroupIDSelector,
  selectAccount,
  selectedGroupNameForParcelsSelector,
  selectedGroupNameSelector,
  selectedGroupSelector,
  userTypeSelector,
} from 'store/feature/accountsInfo/accountsInfo.selector.ts';
import {
  incidentForParcelSyncSelector,
  selectedIncidentSelector,
  userIncidentsSelector,
} from 'store/feature/incidents/incidents.selector.ts';
import { RadioSelectOptions } from 'interfaces/SelectOptions.interface.ts';
import { useSideMenuForm } from 'hooks/index.ts';
import { FieldName } from 'hooks/useSideMenu.ts';
import { Nullable } from 'interfaces/Nullable.ts';
import ModalSelect from 'components/primitive/DrawerMenu/ModalSelect.tsx';
import {
  fetchJPDAIncidentSetting,
  saveSelectedIncidentSetting,
  updateDataSyncIncident,
} from 'store/feature/incidents/incidents.thunk.ts';
import { updatedSelectedGroupForMapForStateUser } from 'store/feature/accountsInfo/accountsInfo.thunk.ts';
import { UserIncidentsType } from 'interfaces/incidents.interface.ts';
import { fetchIADataSet } from 'store/feature/ia/ia.thunk.ts';
import { fetchPADataSet } from 'store/feature/pa/pa.thunk.ts';
import { accessTypeSelector } from 'store/feature/authToken/authToken.selector.ts';
import {
  applicantsSelector,
  selectedApplicantSelector,
} from 'store/feature/applicants/applicants.selector.ts';
import { SitePermissionTypes, UserType } from '@/common/enum';
import { ApplicantType } from 'interfaces/applicants.interface.ts';
import { updateSelectedApplicantSettings } from 'store/feature/applicants/applicants.thunk.ts';
import { resetAccountsInfo } from 'store/feature/accountsInfo/accountsInfo.actions.ts';
import { resetIncidents } from 'store/feature/incidents/incidents.actions.ts';
import { resetApplicant } from 'store/feature/applicants/applicants.action.ts';
import { fetchIAHomePage, fetchPAHomePage } from 'store/feature/dashboard/dashboard.thunk.ts';
import ModalJPDASelectGroup from './ModalJPDA.tsx';
import SideMenuSettings, { SettingsRow } from './SideMenuSettings.tsx';
import useJPDAWorkflow, { JPDAActionType, JPDASettingType } from './useJPDAWorkflow.tsx';
import {
  USER_GUIDE_URL,
  buildSideMenuItems,
  sideMenuAction,
  sideMenuActivePath,
} from './sideMenu.tsx';

// eti-ui's bundle loads only when someone first opens Contact Support, not with every page.
const ModalContactSupport = lazy(() => import('./ModalContactSupport.tsx'));

/**
 * The Damage Assessment side menu: @zawarski/palmetto-react-side-menu, the drawer the sister
 * Palmetto apps use. Any page header opens it with the `toggle-sidebar` window event. Its footer
 * holds the Settings panel (incident, county, applicant, JPDA); the selection modals, the JPDA modal
 * and the Contact Support form render beside it, after the menu has closed, because the open menu
 * traps focus.
 */
const DrawerMenu: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const form = useSideMenuForm();
  const jpda = useJPDAWorkflow();
  const selectedGroupID = useAppSelector(accountGroupIDSelector);
  const allGroups = useAppSelector(allGroupsSelector);
  const selectedGroup = useAppSelector(selectedGroupSelector);
  const userIncidents = useAppSelector(userIncidentsSelector);
  const selectedIncidentForParcelSync = useAppSelector(incidentForParcelSyncSelector);
  const parentGroupID = useAppSelector(parentGroupIDSelector);
  const selectGroupName = useAppSelector(selectedGroupNameSelector) ?? 'No Group';
  const selectedParentGroupName = useAppSelector(selectedGroupNameForParcelsSelector) ?? 'No Group';
  const selectedIncident = useAppSelector(selectedIncidentSelector);
  const incidentName = (selectedIncident && selectedIncident?.pvIncidentName) ?? 'No Incident';
  const account = useAppSelector(selectAccount);
  const userType = useAppSelector(userTypeSelector);
  const accessType = useAppSelector(accessTypeSelector);
  const applicantsDataSet = useAppSelector(applicantsSelector);
  const selectedApplicant = useAppSelector(selectedApplicantSelector);

  const applicantsData = useMemo(() => applicantsDataSet ?? [], [applicantsDataSet]);
  const isPalmettoAdmin = accessType === SitePermissionTypes.A;
  const isPalmettoFinance = accessType === SitePermissionTypes.F;
  const isStateUser = userType === UserType.STATE_USER;
  // Until the access type loads every entry shows, as the legacy drawer did.
  const showRestricted = !accessType || isPalmettoAdmin || isPalmettoFinance;
  const applicantName = (selectedApplicant && selectedApplicant.applicant_name) ?? 'No Applicant';

  const activeGroup = selectedGroup ? selectedGroup : selectedGroupID;
  const userFullName =
    account !== null ? `${account?.pvPersonGivenName} ${account?.pvPersonSurName}` : '';
  const userIncidentData =
    userIncidents &&
    userIncidents.length > 0 &&
    userIncidents.find(
      (item) => Number(item.pvIncidentID) === Number(selectedIncidentForParcelSync)
    )?.pvIncidentName;
  const dataGatheringSync = userIncidentData ? userIncidentData : 'No Incident Selected';

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [subMenuOpen, setSubMenuOpen] = useState<boolean>(false);
  const [contactSupportOpen, setContactSupportOpen] = useState<boolean>(false);
  const [contactSupportLoaded, setContactSupportLoaded] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<Nullable<FieldName>>(null);
  const [processingData, setProcessingData] = useState({
    group: false,
    incident: false,
    sync: false,
    applicant: false,
  });

  const menuItems = useMemo(
    () =>
      buildSideMenuItems({
        showRestricted,
        userFullName,
        iconColor: theme.palette.primary.dark,
        logoutColor: theme.palette.error.main,
        mutedColor: theme.palette.grey[400],
      }),
    [showRestricted, userFullName, theme]
  );

  const closeMenu = () => {
    setIsDrawerOpen(false);
    setSubMenuOpen(false);
  };

  useEffect(() => {
    function handleToggle() {
      setIsDrawerOpen((prevState) => !prevState);
      setSubMenuOpen(false);
    }

    window.addEventListener('toggle-sidebar', handleToggle);
    return () => window.removeEventListener('toggle-sidebar', handleToggle);
  }, []);

  useEffect(() => {
    if (userIncidents) {
      const incidentOptions: RadioSelectOptions[] = userIncidents
        .map((incident) => {
          return { label: incident.pvIncidentName, value: incident.pvIncidentID };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
      form.setValue('incidentOptions', incidentOptions);
      if (selectedIncident) form.setValue('incident', selectedIncident?.pvIncidentID);
      if (selectedIncidentForParcelSync)
        form.setValue('dataGathering', Number(selectedIncidentForParcelSync));
    }
  }, [userIncidents, selectedIncident, selectedIncidentForParcelSync, form]);

  useEffect(() => {
    if (allGroups && allGroups.length) {
      const groupOptions: RadioSelectOptions[] = allGroups
        .map((group) => {
          return { label: group.pvGroupName, value: group.pvGroupID };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
      form.setValue('groupOptions', groupOptions);
      form.setValue('group', Number(activeGroup));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allGroups, selectedGroup, selectedGroupID, form]);

  useEffect(() => {
    if (applicantsData && applicantsData.length) {
      const applicantOptions: RadioSelectOptions[] = applicantsData
        .map((applicant) => {
          return {
            label: applicant.applicant_name ?? '',
            value: applicant.id ?? '',
            withIndicator: Boolean(applicant.state_wide),
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
      form.setValue('applicantsOptions', applicantOptions);
      if (selectedApplicant && selectedApplicant.id)
        form.setValue('applicants', selectedApplicant.id);
    }
  }, [selectedApplicant, applicantsData, form]);

  const handleLogOut = () => {
    dispatch(resetAuthToken());
    dispatch(resetAccountsInfo());
    dispatch(resetIncidents());
    dispatch(resetApplicant());
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = '/';
  };

  const handleMenuClick = (path: string) => {
    switch (sideMenuAction(path)) {
      case 'logout':
        return handleLogOut();
      case 'toggleSubMenu':
        // The PA entry opens its pages in place; the menu stays open.
        return setSubMenuOpen((prevState) => !prevState);
      case 'userGuide':
        window.open(USER_GUIDE_URL, '_blank', 'noopener,noreferrer');
        return closeMenu();
      case 'contactSupport':
        // The menu closes first so the form can take focus over the current page.
        closeMenu();
        setContactSupportLoaded(true);
        return setContactSupportOpen(true);
      case 'navigate':
        closeMenu();
        return navigate(path);
      case 'close':
        return closeMenu();
    }
  };

  const openSelectModal = (fieldName: FieldName) => {
    closeMenu();
    form.setValue('fieldName', fieldName);
    setActiveModal(fieldName);
    form.setValue('isOpen', true);
  };

  const handleJPDAAction = (settingType: JPDASettingType, actionType: JPDAActionType) => {
    if (jpda.open(settingType, actionType)) closeMenu();
  };

  const settingsRows: SettingsRow[] = [
    {
      key: 'incident',
      title: 'Data Viewing Incident',
      subtitle: incidentName,
      icon: <TriangleAlert size={20} />,
      enabled: true,
      onClick: () => openSelectModal(FieldName.incident),
    },
    {
      key: 'dataGathering',
      title: 'Data Gathering Incident',
      subtitle: dataGatheringSync,
      icon: <CircleDashed size={20} />,
      enabled: isPalmettoAdmin,
      onClick: () => openSelectModal(FieldName.dataGathering),
    },
    {
      key: 'group',
      title: parentGroupID ? selectedParentGroupName : selectGroupName,
      subtitle: 'County',
      icon: <Users size={20} />,
      enabled: isStateUser,
      onClick: () => openSelectModal(FieldName.group),
    },
    {
      key: 'applicant',
      title: applicantName,
      subtitle: 'Applicant',
      icon: <SquareUser size={20} />,
      enabled: true,
      onClick: () => openSelectModal(FieldName.applicants),
    },
  ];

  const processSelectedGroup = async (groupID: number) => {
    return await dispatch(updatedSelectedGroupForMapForStateUser(groupID)).unwrap();
  };

  const processDataSyncIncidentSave = async (payload: {
    incidentID: number;
    incidentName: string;
  }) => {
    return await dispatch(updateDataSyncIncident(payload)).unwrap();
  };

  const processIncidentSettingSave = async (payload: UserIncidentsType) => {
    return await dispatch(saveSelectedIncidentSetting(payload)).unwrap();
  };

  const processSelectedApplicant = async (applicant: ApplicantType) => {
    return await dispatch(updateSelectedApplicantSettings(applicant)).unwrap();
  };

  const handleModal = (isOpen: boolean) => {
    const selectedField = form.getValues('fieldName');
    if (!isOpen) {
      if (selectedField === FieldName.incident && selectedIncident) {
        form.setValue('incident', selectedIncident.pvIncidentID);
      }
      if (selectedField === FieldName.group && selectedGroupID) {
        form.setValue('group', Number(selectedGroupID));
      }
      if (selectedField === FieldName.dataGathering && selectedIncidentForParcelSync) {
        form.setValue('dataGathering', Number(selectedIncidentForParcelSync));
      }
    }
    form.setValue('isOpen', isOpen);
  };

  function validateIncidentExists(incidentID: number): number {
    if (!userIncidents) return -1;
    return userIncidents.findIndex((item) => Number(item.pvIncidentID) === incidentID);
  }

  const handleModalAction = (fieldName: FieldName) => {
    const data = form.getValues();
    if (fieldName === FieldName.incident && userIncidents) {
      // *: Validate if incident exists in the userIncidents
      const found = validateIncidentExists(Number(data.incident));
      if (found === -1) return;
      const incident = userIncidents[found];
      if (!incident) return;
      setProcessingData((prevState) => ({ ...prevState, incident: true }));
      processIncidentSettingSave(incident).then(() => {
        dispatch(fetchIADataSet({ incidentID: incident.pvIncidentID }));
        dispatch(fetchPADataSet({ incidentID: incident.pvIncidentID }));
        if (isStateUser) dispatch(fetchIAHomePage({ selectedGroupIndex: 0 }));
        else dispatch(fetchIAHomePage({ selectedGroupIndex: Number(activeGroup) }));
        dispatch(fetchPAHomePage());
        setTimeout(() => {
          setProcessingData((prevState) => ({ ...prevState, incident: false }));
          form.setValue('isOpen', false);
        }, 2000);
      });
    }
    if (fieldName === FieldName.dataGathering && userIncidents) {
      // *: Validate if incident exists in the userIncidents
      const found = validateIncidentExists(Number(data.dataGathering));
      if (found === -1) return;
      const incident = userIncidents[found];
      if (!incident) return;
      setProcessingData((prevState) => ({ ...prevState, sync: true }));
      processDataSyncIncidentSave({
        incidentID: Number(data.dataGathering),
        incidentName: incident.pvIncidentName,
      }).then(() => {
        setTimeout(() => {
          setProcessingData((prevState) => ({ ...prevState, sync: false }));
          form.setValue('isOpen', false);
        }, 2000);
      });
    }
    if (fieldName === FieldName.group) {
      const groupID = Number(data.group);
      setProcessingData((prevState) => ({ ...prevState, group: true }));
      processSelectedGroup(groupID)
        .then(() => {
          dispatch(fetchIAHomePage({ selectedGroupIndex: Number(activeGroup) }));
          dispatch(fetchPAHomePage());
          setTimeout(() => {
            setProcessingData((prevState) => ({ ...prevState, group: false }));
            form.setValue('isOpen', false);
          }, 2000);
        })
        .catch((error) => {
          console.error('Group selection save failed', error);
          setProcessingData((prevState) => ({ ...prevState, group: false }));
        });
    }
    if (fieldName === FieldName.applicants) {
      const applicantID = String(data.applicants);
      const applicantData = applicantsData.find((item) => item.id === applicantID);
      if (applicantData) {
        setProcessingData((prevState) => ({ ...prevState, applicant: true }));
        processSelectedApplicant(applicantData).then(() => {
          setTimeout(() => {
            setProcessingData((prevState) => ({ ...prevState, applicant: false }));
            form.setValue('isOpen', false);
          }, 2000);
        });
      }
    }
  };

  return (
    <>
      <PalmettoSideMenu
        srcLogo={Logo}
        programTitle='Damage Assessment'
        menuItems={menuItems}
        isDrawerOpen={isDrawerOpen}
        activePath={sideMenuActivePath(pathname, subMenuOpen)}
        activeSubMenu={subMenuOpen}
        deactivateSubMenu={() => setSubMenuOpen(false)}
        onDrawerClose={closeMenu}
        onMenuClick={handleMenuClick}
        footer={
          <SideMenuSettings
            rows={settingsRows}
            jpda={isStateUser ? { counts: jpda.counts, onAction: handleJPDAAction } : undefined}
            onExpand={() => dispatch(fetchJPDAIncidentSetting())}
          />
        }
      />
      <FormProvider {...form}>
        {activeModal === FieldName.incident ? (
          <ModalSelect
            headerTitle={'Select Data Viewing Incident'}
            isSaving={processingData.incident}
            optionData={'incidentOptions'}
            onClose={handleModal}
            onConfirm={handleModalAction}
          />
        ) : null}
        {activeModal === FieldName.dataGathering ? (
          <ModalSelect
            headerTitle={'Select Data Gathering Incident'}
            isSaving={processingData.sync}
            optionData={'incidentOptions'}
            onClose={handleModal}
            onConfirm={handleModalAction}
          />
        ) : null}
        {activeModal === FieldName.group ? (
          <ModalSelect
            headerTitle={'Select Group'}
            isSaving={processingData.group}
            optionData={'groupOptions'}
            onClose={handleModal}
            onConfirm={handleModalAction}
          />
        ) : null}
        {activeModal === FieldName.applicants ? (
          <ModalSelect
            headerTitle={'Select Applicant'}
            isSaving={processingData.applicant}
            optionData={'applicantsOptions'}
            onClose={handleModal}
            onConfirm={handleModalAction}
            withFilter
          />
        ) : null}
      </FormProvider>
      <ModalJPDASelectGroup {...jpda.modalProps} />
      {contactSupportLoaded ? (
        <Suspense fallback={null}>
          <ModalContactSupport open={contactSupportOpen} onOpenChange={setContactSupportOpen} />
        </Suspense>
      ) : null}
    </>
  );
};

export default DrawerMenu;
